import { http } from "../httpHandler";
import { ConversationTask, TServerErrorResponse } from "@/types";
import { links } from "@/constants";
import type { TaskRecipientType } from "@/components/common/SearchableSelect/SearchableSelectCrm";

export const GET_CONVERSATION_TASKS = "GET_CONVERSATION_TASKS";
const VALID_RECIPIENT_TYPES = new Set<TaskRecipientType>([
  "CONTACT",
  "ACCOUNT",
  "LEAD",
]);

const validateRecipientType = (
  type?: string,
): TaskRecipientType | undefined => {
  if (!type) return undefined;
  if (VALID_RECIPIENT_TYPES.has(type as TaskRecipientType)) {
    return type as TaskRecipientType;
  }
  return undefined;
};

export const createConversationTask = async (
  task: Partial<ConversationTask>,
) => {
  try {
    const response = await http.post<ConversationTask>(
      links.conversationTask.index,
      task,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const updateConversationTask = async (
  task: Partial<ConversationTask>,
) => {
  const { id, createdAt, conversationId, updatedAt, userId, ...data } = task;
  try {
    const response = await http.put<ConversationTask>(
      links.conversationTask.conversationTaskById(id!),
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const bulkUpdateConversationTask = async ({
  taskIds,
  task,
}: {
  taskIds: string[];
  task: Partial<ConversationTask>;
}) => {
  try {
    const response = await http.put<ConversationTask[]>(
      links.conversationTask.index,
      { crmContactId: task.crmContactId },
      {
        params: {
          tasks: taskIds.join(","),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const deleteConversationTask = async (taskId: string) => {
  try {
    const response = await http.delete<ConversationTask>(
      links.conversationTask.conversationTaskById(taskId),
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const bulkDeleteConversationTask = async (taskIds: string[]) => {
  try {
    const response = await http.delete<ConversationTask>(
      links.conversationTask.index,
      { params: { tasks: taskIds } },
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getConversationTasks = async (conversationId?: string) => {
  if (!conversationId) return;
  try {
    const response = await http.get<ConversationTask[]>(
      links.conversationTask.conversationTaskByConversationId(conversationId),
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const pushTaskToCrm = async (
  conversationTaskId?: string,
  recipientType?: TaskRecipientType,
) => {
  if (!conversationTaskId) return;
  const validatedRecipientType = validateRecipientType(recipientType);
  try {
    const response = await http.post<ConversationTask>(
      links.crm.pushTask(conversationTaskId),
      validatedRecipientType
        ? { recipientType: validatedRecipientType }
        : undefined,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};
